import React, { useEffect, useState } from "react";
import {
    Input,
    Form,
    Col,
    Button,
    message,
    Table,
    Row,
    Tooltip,
    Popconfirm,
    Select,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Layouts from "../components/Layouts";
import apiRequest from "../hooks/ApiRequest";

const { Option } = Select;

const StampDuty = (props) => {
    const { form } = props;
    const { getFieldDecorator, setFieldsValue, resetFields, validateFields } = form;

    const [stampDutyList, setStampDutyList] = useState([]);
    const [stampDutyId, setStampDutyId] = useState(0);
    const [editRecord, setEditRecord] = useState(null);
    const [insurerProducts, setInsurerProducts] = useState([]);
    const LoginInsurerId = JSON.parse(localStorage.getItem("loggedInUserID"));

    const columns = [
        {
            key: "1",
            title: "Product Name",
            dataIndex: "specificProductId",
            render: (productId) => {
                if (productId === 0) return "All";
                const product = insurerProducts.find((p) => p.productId === productId);
                return product ? product.productName : "-";
            },
        },
        {
            key: "2",
            title: "Tax Name",
            dataIndex: "taxName",
        },
        {
            key: "3",
            title: "Rate",
            render: (record) =>
                record.rateType === "$"
                    ? `$${record.ratePercentage}`
                    : `${record.ratePercentage}%`,
        },
        {
            key: "4",
            title: "Action",
            render: (_, record) => (
                <>
                    <Tooltip title="Edit">
                        <Button
                            type="primary"
                            className="edit_btn"
                            onClick={() => handleEditStampDuty(record)}
                        >
                            <EditOutlined />
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        className="delete_btn"
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDeleteStampDuty(record.id)}
                    >
                        <Tooltip title="Delete">
                            <Button type="danger" style={{ marginLeft: 8 }}>
                                <DeleteOutlined />
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </>
            ),
        },
    ];

    const fetchStampDuties = async () => {
        try {
            const loggedInInsurerId = localStorage.getItem("insurerId");
            if (!loggedInInsurerId) {
                message.error("Logged in insurer not found!");
                return;
            }

            const response = await apiRequest(
                `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/GetStampByInsurerId/${loggedInInsurerId}`,
                "GET"
            );

            if (response.success) {
                const list = (response.data || []).map((item) => ({
                    ...item,
                    rateType:
                        item.rateType ||
                        (item.type === "Fixed Amount" ? "$" : "%"), // derive from backend field
                }));

                setStampDutyList(list);
            } else {
                message.error(response.message || "No stamp duties found!");
            }
        } catch (error) {
            console.error("Fetch Stamp Duties Error:", error);
            message.error("Failed to fetch stamp duties!");
        }
    };

    const fetchInsurerProducts = async () => {
        try {
            const loggedInInsurerId = localStorage.getItem("insurerId");
            if (!loggedInInsurerId) {
                message.error("Logged in insurer not found!");
                return;
            }

            const response = await apiRequest(
                `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/GetInsurerProductByInsurerId/${loggedInInsurerId}`,
                "GET"
            );

            if (response.success) {
                setInsurerProducts(response.data || []);
            } else {
                message.error(response.message || "No insurer products found!");
            }
        } catch (error) {
            console.error("Fetch Insurer Products Error:", error);
            message.error("Failed to fetch insurer products!");
        }
    };

    useEffect(() => {
        fetchStampDuties();
        fetchInsurerProducts();
    }, []);

    useEffect(() => {
        if (editRecord && editRecord.id === stampDutyId) {
            setFieldsValue({
                productName: editRecord.specificProductId,
                taxType: editRecord.taxName,
                rate: editRecord.ratePercentage,
                rateType: editRecord.rateType || "%",
            });
        }
    }, [stampDutyId, editRecord, setFieldsValue]);

    const handleEditStampDuty = (record) => {
        setEditRecord(record);
        setStampDutyId(record.id);
    };

    const handleDeleteStampDuty = async (id) => {
        try {
            const res = await apiRequest(
                `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/DeleteStampById/${id}`,
                "POST"
            );
            if (res.success) {
                message.success("Stamp Duty Deleted Successfully!");
                fetchStampDuties();
            } else {
                message.error("Failed to delete stamp duty.");
            }
        } catch (error) {
            console.error("Delete Stamp Duty Error:", error);
            message.error("Failed to delete stamp duty!");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const values = await validateFields();
            const { productName, taxType, rate, rateType } = values;

            const payload = {
                id: stampDutyId,
                specificProductId: productName,
                taxName: taxType,
                ratePercentage: parseFloat(rate),
                insurerId: parseInt(LoginInsurerId),
                type: rateType === "$" ? "Fixed Amount" : "Percentage",
            };

            const url = stampDutyId
                ? `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/UpdateStampDuty/${stampDutyId}`
                : `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/CreateStampDuty`;

            const response = await apiRequest(url, "POST", payload);

            if (response.success) {
                message.success(
                    stampDutyId
                        ? "Stamp Duty Updated Successfully!"
                        : "Stamp Duty Added Successfully!"
                );

                // Store type in local list for UI
                const updatedList = [...stampDutyList];
                if (stampDutyId) {
                    const index = updatedList.findIndex((i) => i.id === stampDutyId);
                    if (index !== -1)
                        updatedList[index] = { ...payload, id: stampDutyId, rateType };
                } else {
                    updatedList.push({ ...payload, id: Date.now(), rateType });
                }

                setStampDutyList(updatedList);
                resetFields();
                setStampDutyId(0);
            } else {
                message.error("Something went wrong!");
            }
        } catch (error) {
            console.error("Submit Stamp Duty Error:", error);
            message.error("Failed to process request!");
        }
    };

    return (
        <Layouts title="Stamp Duty" classname="dashboard">
            <div className="dash-bg-white">
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <div className="section-top-heading">
                            <h3 style={{ color: "#0C1362", fontWeight: "600", fontSize: "20px" }}>
                                Manage Stamp Duty
                            </h3>
                        </div>
                    </Col>
                </Row>
            </div>

            <div>
                <Row className="m-t-30 m-b-40">
                    <div className="dash-bg-white" style={{ padding: "20px", width: "100%" }}>
                        <Form layout="vertical" onSubmit={handleSubmit}>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={6}>
                                    <Form.Item label="Product Name">
                                        {getFieldDecorator("productName", {
                                            initialValue: 0,
                                            rules: [{ required: true, message: "Please select product name" }],
                                        })(
                                            <Select placeholder="Select Product">
                                                <Option key={0} value={0}>
                                                    All
                                                </Option>
                                                {insurerProducts.map((prod) => (
                                                    <Option key={prod.productId} value={prod.productId}>
                                                        {prod.productName}
                                                    </Option>
                                                ))}
                                            </Select>
                                        )}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={6}>
                                    <Form.Item label="Tax Type">
                                        {getFieldDecorator("taxType", {
                                            rules: [{ required: true, message: "Please enter tax type" }],
                                        })(<Input placeholder="Enter Tax Type" />)}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={6}>
                                    <Form.Item label="Rate">
                                        <Input.Group compact>
                                            {getFieldDecorator("rate", {
                                                rules: [
                                                    { required: true, message: "Please enter rate" },
                                                    {
                                                        validator: (_, value, cb) => {
                                                            if (!value) cb();
                                                            else if (Number(value) <= 0)
                                                                cb("Rate must be greater than 0");
                                                            else cb();
                                                        },
                                                    },
                                                ],
                                            })(
                                                <Input
                                                    style={{ width: "70%" }}
                                                    type="number"
                                                    min={0}
                                                    step="0.01"
                                                    placeholder="Enter Rate"
                                                />
                                            )}
                                            {getFieldDecorator("rateType", {
                                                initialValue: "%",
                                            })(
                                                <Select style={{ width: "30%" }}>
                                                    <Option value="%">%</Option>
                                                    <Option value="$">$</Option>
                                                </Select>
                                            )}
                                        </Input.Group>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={6}>
                                    <Button htmlType="submit" className="primary m-t-22" block>
                                        {stampDutyId ? "Update Duty" : "Add Duty"}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <div className="table-grid-bx">
                        <Table
                            className="table-scroll-auto"
                            columns={columns}
                            dataSource={stampDutyList}
                            pagination={false}
                            rowKey="id"
                        />
                    </div>
                </Row>
            </div>
        </Layouts>
    );
};

export default Form.create()(StampDuty);
